import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/EmptyState'
import { SearchEmptyState } from '@/components/shared/SearchEmptyState'
import { SearchBar } from '@/components/shared/SearchBar'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { Card, CardContent } from '@/components/ui/card'
import { useListQuery } from '@/hooks/useListQuery'
import { useTagsList } from '@/hooks/useTagsList'

export function TagManagementPage() {
  const navigate = useNavigate()
  const listQuery = useListQuery()

  // Fetch tags data with search and pagination
  const { data, isLoading, isError, error, refetch } = useTagsList(
    listQuery.filters
  )

  // Derive states
  const isEmpty = !isLoading && data?.tags.length === 0 && !listQuery.searchText
  const isSearchEmpty =
    !isLoading && data?.tags.length === 0 && listQuery.searchText

  const handleCreateTag = () => {
    navigate('/tags/new')
  }

  const handleTagClick = (tagId: string) => {
    navigate(`/tags/${tagId}`)
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {/* Search Bar + Create Button */}
        {!isEmpty && (
          <div className="mb-6 flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={listQuery.searchText}
                onChange={listQuery.setSearchText}
                placeholder="Search tags..."
                ariaLabel="Search tags"
                className="max-w-md"
              />
            </div>
            <Button
              onClick={handleCreateTag}
              size="sm"
              className="bg-[#0033A0] hover:bg-[#002080]"
              aria-label="Create new tag"
            >
              + Create Tag
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingSpinner size="lg" />}

        {/* Error State */}
        {isError && (
          <ErrorState
            error={error || new Error('Failed to load tags')}
            onRetry={refetch}
            message="Failed to load tags"
          />
        )}

        {/* Empty State */}
        {!isLoading && !isError && isEmpty && (
          <EmptyState
            icon="🏷️"
            title="No tags yet"
            description="Create your first tag to organize your moves and series"
            actionLabel="Create First Tag"
            onAction={handleCreateTag}
          />
        )}

        {/* Search Empty State */}
        {!isLoading && !isError && isSearchEmpty && (
          <SearchEmptyState
            searchText={listQuery.searchText}
            onClearSearch={listQuery.clearSearch}
            resourceType="tags"
          />
        )}

        {/* Tags Grid */}
        {!isLoading && !isError && !isEmpty && !isSearchEmpty && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data?.tags.map(tag => (
              <Card
                key={tag.id}
                role="article"
                aria-label={`Tag: ${tag.name}`}
                className="cursor-pointer transition-all hover:shadow-md"
                onClick={() => handleTagClick(tag.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Color Indicator */}
                    <div
                      className="mt-1 h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: tag.color || '#6B7280' }}
                    />

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <h3 className="mb-1 text-base font-semibold text-black">
                        {tag.name}
                      </h3>

                      {tag.description && (
                        <p className="mb-2 line-clamp-2 text-sm text-[#666666]">
                          {tag.description}
                        </p>
                      )}

                      <div className="text-xs text-[#666666]">
                        Created {new Date(tag.createdAt).toLocaleDateString()}
                      </div>

                      {/* Usage counts */}
                      <div className="mt-2 text-xs text-[#666666]">
                        {!('moveCount' in tag) ||
                        ((tag as { moveCount?: number }).moveCount === 0 &&
                          (tag as { seriesCount?: number }).seriesCount ===
                            0) ? (
                          <span>Not used yet</span>
                        ) : (
                          <>
                            {(tag as { moveCount?: number }).moveCount &&
                              (tag as { moveCount?: number }).moveCount! >
                                0 && (
                                <span>
                                  {(tag as { moveCount?: number }).moveCount}{' '}
                                  {(tag as { moveCount?: number }).moveCount ===
                                  1
                                    ? 'move'
                                    : 'moves'}
                                </span>
                              )}

                            {(tag as { moveCount?: number }).moveCount &&
                              (tag as { moveCount?: number }).moveCount! > 0 &&
                              (tag as { seriesCount?: number }).seriesCount &&
                              (tag as { seriesCount?: number }).seriesCount! >
                                0 && <span className="mx-2">•</span>}

                            {(tag as { seriesCount?: number }).seriesCount &&
                              (tag as { seriesCount?: number }).seriesCount! >
                                0 && (
                                <span>
                                  {
                                    (tag as { seriesCount?: number })
                                      .seriesCount
                                  }{' '}
                                  {(tag as { seriesCount?: number })
                                    .seriesCount === 1
                                    ? 'series'
                                    : 'series'}
                                </span>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
